import { ArrowLeft } from "lucide-react";
import React from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { Button } from "@/components";
import {
  useBlockPartnerListBack,
  useScopedPartnerNav,
} from "@/context/auth/scoped-partner-nav";
import { useI18n } from "@/context/i18n";

import { useClients } from "./clients.hooks";
import { TClientExtendedResponse } from "./clients.types";
import { ClientReviewData } from "./components/client-review-data";

export const ClientDetailPage: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId: string }>();
  const { isScopedPartner, organizationRootId } = useScopedPartnerNav(clientId);

  useBlockPartnerListBack(isScopedPartner, organizationRootId);

  if (
    isScopedPartner &&
    organizationRootId &&
    clientId &&
    clientId !== organizationRootId
  ) {
    return <Navigate to={`/clients/${organizationRootId}`} replace />;
  }

  const {
    client,
    isLoading,
    deleteClient,
    selectClient,
    setIsClientReviewOpen,
    setSelectedClient,
  } = useClients(clientId);

  const handleCloseReview = React.useCallback(
    (open: boolean) => {
      if (!open) {
        if (isScopedPartner) {
          navigate("/dashboard", { replace: true });
          return;
        }
        navigate("/clients");
      }
      setIsClientReviewOpen(open);
    },
    [isScopedPartner, navigate, setIsClientReviewOpen],
  );

  return (
    <div className="flex flex-col w-full h-full space-y-6 p-4">
      <div className="flex flex-row items-start justify-between">
        <div className="flex flex-col w-full px-2">
          <div className="flex items-center gap-3 mb-3">
            {!isScopedPartner && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate("/clients")}
                size="sm"
                className="p-3 hover:bg-muted/50 transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}
            <h1 className="text-3xl font-bold tracking-tight text-foreground">
              {client?.name ?? t("clients.title")}
            </h1>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center p-8">
          <p className="text-muted-foreground">{t("generic.loading")}</p>
        </div>
      ) : client ? (
        <ClientReviewData
          client={client as TClientExtendedResponse}
          setIsClientReviewOpen={handleCloseReview}
          setSelectedClient={setSelectedClient}
          deleteClient={isScopedPartner ? undefined : deleteClient}
          selectClient={isScopedPartner ? undefined : selectClient}
        />
      ) : null}
    </div>
  );
};
